
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2 } from 'lucide-react';

interface UploadedDocument {
  name: string;
  url: string;
}

interface MultipleDocumentUploadProps {
  id: string;
  label: string;
  maxDocuments: number;
  currentFiles?: string[] | null;
  onUpload: (files: File[]) => void;
  onRemove: (index: number) => void;
}

export function MultipleDocumentUpload({
  id,
  label,
  maxDocuments,
  currentFiles = [],
  onUpload,
  onRemove,
}: MultipleDocumentUploadProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Convert current file URLs to display format
  const documents: UploadedDocument[] = Array.isArray(currentFiles) 
    ? currentFiles.map((url, index) => ({
        name: `Document ${index + 1}`,
        url: url
      }))
    : [];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const remainingSlots = maxDocuments - documents.length;
      const filesToUpload = files.slice(0, remainingSlots);
      onUpload(filesToUpload);
      setSelectedFiles([]);
      // Clear the input
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      
      {/* Display existing documents */}
      {documents.length > 0 && (
        <div className="space-y-2">
          {documents.map((doc, index) => (
            <div key={index} className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded">
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex-1 truncate"
              >
                {doc.name}
              </a>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemove(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* File upload input */}
      {documents.length < maxDocuments && (
        <div className="space-y-2">
          <Input
            id={id}
            type="file"
            multiple
            onChange={handleFileChange}
            className="cursor-pointer"
          />
          <p className="text-sm text-muted-foreground">
            {documents.length} documents uploaded
          </p>
        </div>
      )}
    </div>
  );
}
